// src/pages/VerifyEmailPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function VerifyEmailPage({ lang = "en", theme = "dark" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "your@email.com";

  // Тексты на английском и французском
  const texts = {
    en: {
      title: "Check your inbox",
      subtitle: "We just sent a 6-digit code to",
      enterCode: "Enter the code below",
      resend: "Didn’t receive the code?",
      resendLink: "Resend code",
      back: "Back to registration",
      continue: "Continue",
      // Новые тексты для доменного чекера
      domainTitle: "Check domain availability (optional)",
      domainPlaceholder: "example.guru",
      checkButton: "Check Domain",
      checking: "Checking...",
      available: "Available!",
      taken: "Already taken",
      price: "Price",
      year: "year",
    },
    fr: {
      title: "Vérifiez votre boîte mail",
      subtitle: "Nous venons d’envoyer un code à 6 chiffres à",
      enterCode: "Saisissez le code ci-dessous",
      resend: "Vous n’avez pas reçu le code ?",
      resendLink: "Renvoyer le code",
      back: "Retour à l’inscription",
      continue: "Continuer",
      domainTitle: "Vérifier la disponibilité du domaine (facultatif)",
      domainPlaceholder: "exemple.guru",
      checkButton: "Vérifier le domaine",
      checking: "Vérification...",
      available: "Disponible !",
      taken: "Déjà pris",
      price: "Prix",
      year: "an",
    },
  };

  const t = texts[lang] || texts.en;

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);

  // === Состояния для GoDaddy Domain Checker ===
  const [domainInput, setDomainInput] = useState("");
  const [domainData, setDomainData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Темы
  const themeVars = theme === "dark"
    ? {
        card: "#16213e",
        border: "#667eea",
        fg: "#ffffff",
        inputBg: "#0f3460",
        inputFg: "#ffffff",
        btnBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        btnFg: "#ffffff",
        muted: "#999999",
        success: "#28a745",
        danger: "#dc3545",
      }
    : {
        card: "#ffffff",
        border: "#667eea",
        fg: "#333333",
        inputBg: "#f8f9fa",
        inputFg: "#333333",
        btnBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        btnFg: "#ffffff",
        muted: "#888888",
        success: "#28a745",
        danger: "#dc3545",
      };

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // === Обработчики для 6-значного кода ===
  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const newCode = [...code];
    newCode[i] = val;
    setCode(newCode);
    if (val && i < 5) inputsRef.current[i + 1].focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) {
      inputsRef.current[i - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (paste) {
      const newCode = paste.split("").concat(["", "", "", "", "", ""]).slice(0, 6);
      setCode(newCode);
      inputsRef.current[Math.min(paste.length - 1, 5)].focus();
    }
  };

  // === Проверка домена через GoDaddy API ===
  const checkDomain = async () => {
    if (!domainInput.trim()) return;

    setLoading(true);
    setError(null);
    setDomainData(null);

    try {
      // ВНИМАНИЕ: В продакшене используйте .env!
      const API_KEY = process.env.REACT_APP_GODADDY_API_KEY;
      const API_SECRET = process.env.REACT_APP_GODADDY_API_SECRET;

      if (!API_KEY || !API_SECRET) {
        throw new Error("GoDaddy API keys are not configured (check .env)");
      }

      const response = await fetch(
        `https://api.godaddy.com/v1/domains/available?domain=${encodeURIComponent(domainInput)}`,
        {
          method: "GET",
          headers: {
            Authorization: `sso-key ${API_KEY}:${API_SECRET}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`GoDaddy API error: ${response.status} – ${errText}`);
      }

      const data = await response.json();
      // GoDaddy возвращает объект или массив — нормализуем
      const result = Array.isArray(data) ? data[0] : data;
      setDomainData(result);
    } catch (err) {
      setError(err.message);
      console.error("Domain check error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDomainSubmit = (e) => {
    e.preventDefault();
    checkDomain();
  };

  return (
    <div
      style={{
        maxWidth: 520,
        margin: "40px auto",
        background: themeVars.card,
        border: `3px solid ${themeVars.border}`,
        borderRadius: 34,
        boxShadow: `0 8px 56px #000a, 0 1.5px 0 ${themeVars.border}`,
        padding: "40px 38px 48px",
        color: themeVars.fg,
        textAlign: "center",
      }}
    >
      {/* === Верификация email === */}
      <h2 style={{ marginBottom: 20, fontSize: 38, fontWeight: 800 }}>
        {t.title}
      </h2>

      <p style={{ fontSize: 18, marginBottom: 8, opacity: 0.9 }}>
        {t.subtitle}
      </p>

      <p style={{ fontSize: 22, fontWeight: 600, marginBottom: 36, color: themeVars.border }}>
        {email}
      </p>

      <div style={{ display: "flex", justifyContent: "center", gap: 14, marginBottom: 36 }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <input
            key={i}
            type="text"
            maxLength={1}
            value={code[i]}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            ref={(el) => (inputsRef.current[i] = el)}
            style={{
              width: 56,
              height: 66,
              textAlign: "center",
              fontSize: 32,
              fontWeight: "bold",
              background: themeVars.inputBg,
              color: themeVars.inputFg,
              border: `2px solid ${themeVars.border}`,
              borderRadius: 18,
              outline: "none",
            }}
            autoComplete="off"
          />
        ))}
      </div>

      <p style={{ fontSize: 17, marginBottom: 28, opacity: 0.85 }}>
        {t.enterCode}
      </p>

      <button
        style={{
          width: "100%",
          background: themeVars.btnBg,
          color: themeVars.btnFg,
          border: "none",
          borderRadius: 22,
          padding: "18px 0 14px",
          fontSize: 27,
          fontWeight: 700,
          cursor: code.join("").length === 6 ? "pointer" : "not-allowed",
          opacity: code.join("").length === 6 ? 1 : 0.6,
          boxShadow: "0 6px 24px #667eea40",
          marginBottom: 32,
        }}
        disabled={code.join("").length !== 6}
        onClick={() => {
          if (code.join("").length === 6) {
            // Здесь будет запрос на верификацию кода
            alert("Code: " + code.join(""));
          }
        }}
      >
        {t.continue}
      </button>

      <div style={{ fontSize: 17, color: themeVars.muted, marginBottom: 20 }}>
        {t.resend}{" "}
        <span style={{ color: themeVars.border, textDecoration: "underline", cursor: "pointer" }}>
          {t.resendLink}
        </span>
      </div>

      <button
        onClick={() => navigate("/register")}
        style={{
          background: "none",
          color: themeVars.fg,
          border: `1px solid ${themeVars.border}`,
          borderRadius: 18,
          padding: "12px 32px",
          fontSize: 16,
          fontWeight: 500,
          cursor: "pointer",
          marginBottom: 40,
        }}
        onMouseEnter={(e) => {
          e.target.style.background = themeVars.btnBg;
          e.target.style.color = themeVars.btnFg;
        }}
        onMouseLeave={(e) => {
          e.target.style.background = "none";
          e.target.style.color = themeVars.fg;
        }}
      >
        ← {t.back}
      </button>

      {/* === GoDaddy Domain Checker (интегрированный) === */}
      <div style={{ marginTop: 40, paddingTop: 30, borderTop: `1px solid ${themeVars.border}50` }}>
        <h3 style={{ fontSize: 20, marginBottom: 16, opacity: 0.9 }}>
          {t.domainTitle}
        </h3>

        <form onSubmit={handleDomainSubmit} style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 20 }}>
          <input
            type="text"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder={t.domainPlaceholder}
            style={{
              padding: "12px 16px",
              width: 220,
              background: themeVars.inputBg,
              color: themeVars.inputFg,
              border: `2px solid ${themeVars.border}`,
              borderRadius: 16,
              fontSize: 16,
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={loading || !domainInput.trim()}
            style={{
              padding: "12px 24px",
              background: loading ? "#666" : themeVars.btnBg,
              color: themeVars.btnFg,
              border: "none",
              borderRadius: 16,
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              minWidth: 140,
            }}
          >
            {loading ? t.checking : t.checkButton}
          </button>
        </form>

        {error && (
          <div style={{
            background: "rgba(220,53,69,0.15)",
            color: themeVars.danger,
            padding: 14,
            borderRadius: 16,
            fontSize: 15,
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {domainData && !loading && (
          <div style={{
            marginTop: 20,
            padding: 20,
            background: themeVars.inputBg,
            border: `2px solid ${domainData.available ? themeVars.success : themeVars.danger}`,
            borderRadius: 20,
            textAlign: "left",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 22, fontWeight: "bold" }}>
              <span style={{ color: domainData.available ? themeVars.success : themeVars.danger }}>
                {domainData.available ? "✓" : "✗"}
              </span>
              <span>{domainData.domain || domainInput}</span>
            </div>

            <div style={{ marginTop: 12, fontSize: 16, opacity: 0.9 }}>
              <div><strong>{t.available}:</strong> {domainData.available ? t.available : t.taken}</div>
              {domainData.price !== undefined && (
                <div>
                  <strong>{t.price}:</strong> {domainData.currency || "USD"} {(domainData.price / 1000000).toFixed(2)} / {domainData.period || 1} {t.year}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}